import type { UseQueryResult } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Panel, PanelBody, PanelHead, StatCard } from "../../components";
import {
  useAiJobCount,
  useBrandCount,
  useComparableGroupCount,
  useDiscountCount,
  usePriceListCount,
  useProductCount,
  useRateCardCount,
  useSupplierCount,
} from "../../api/stats";

function statValue(query: UseQueryResult<{ total: number }>): string {
  if (query.data) return String(query.data.total);
  return query.isError ? "—" : "…";
}

/** Landing screen: live entity counts pulled from the list endpoints' totals. */
export function DashboardPage() {
  const suppliers = useSupplierCount();
  const brands = useBrandCount();
  const rateCards = useRateCardCount();
  const discounts = useDiscountCount();
  const products = useProductCount();
  const priceLists = usePriceListCount();
  const comparableGroups = useComparableGroupCount();
  const aiJobs = useAiJobCount();

  return (
    <div className="stack">
      <div className="grid-4">
        <StatCard label="Suppliers" value={statValue(suppliers)} sub="Active merchant accounts" />
        <StatCard label="Brands" value={statValue(brands)} sub="Brands + aliases in the catalogue" />
        <StatCard label="Rate cards" value={statValue(rateCards)} sub="Terms codes across suppliers" />
        <StatCard label="Discounts" value={statValue(discounts)} sub="Standing discount rules" />
      </div>
      <div className="grid-4">
        <StatCard label="Products" value={statValue(products)} sub="Canonical catalogue lines" />
        <StatCard label="Price lists" value={statValue(priceLists)} sub="Versioned ingests" />
        <StatCard
          label="Comparable groups"
          value={statValue(comparableGroups)}
          sub="Cross-brand buckets"
        />
        <StatCard label="AI jobs" value={statValue(aiJobs)} sub="Logged model calls" />
      </div>
      <Panel>
        <PanelHead
          title="Getting started"
          sub="catalogue first — price lists, comparables and pricings build on it"
        />
        <PanelBody>
          <div className="line-list">
            <div className="line-row">
              <span>
                <strong>1. Set up the catalogue</strong>
                <span className="sub">
                  Add <Link to="/suppliers">suppliers</Link> (with branches),{" "}
                  <Link to="/brands">brands</Link>, <Link to="/rate-cards">rate cards</Link> and{" "}
                  <Link to="/discounts">discounts</Link>.
                </span>
              </span>
            </div>
            <div className="line-row">
              <span>
                <strong>2. Ingest price lists</strong>
                <span className="sub">
                  Upload or paste supplier files on <Link to="/price-lists">Price lists</Link> —
                  screen landing shortly in this build.
                </span>
              </span>
            </div>
            <div className="line-row">
              <span>
                <strong>3. Price a project</strong>
                <span className="sub">
                  Create a <Link to="/projects">project</Link>, add a named pricing and paste a
                  buy-list to compare suppliers.
                </span>
              </span>
            </div>
          </div>
        </PanelBody>
      </Panel>
    </div>
  );
}
